import { convertLocalTime } from "@common/utils/localTime.utils";
import {
  MeetingInstanceFilters,
  ScheduledMeetingProducerService,
} from "@services/meetings/types";
import {
  asBoolean,
  asNonEmptyArray,
  asNumber,
  asString,
  objectToSearchParams,
  SLFilterToSearch,
} from "./common";
import { getMappedValue } from "./const";

const inProgressFilter = ["InProgress", "InProgressPartial"];
const certificationFilter = [
  "CertificationOfManaged",
  "CertificationOfUnmanaged",
];

export const TIME_FILTER_SEARCH_KEY = "time";

export const getTimesForMeetingsFilter = (
  filter: MeetingsTimeFilter,
  payload?: DateInterval
): [start: number, end: number] => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter as MeetingsTimeFilter) {
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
    case "For the last 12 hours":
      start.setHours(end.getHours() - 12);
      break;
    case "For the last 24 hours":
      start.setDate(end.getDate() - 1);
      break;
    case "Today":
      start.setHours(0, 0, 0, 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "For the next 12 hours":
      end.setHours(start.getHours() + 12);
      break;
    case "For the next 24 hours":
      end.setDate(start.getDate() + 1);
      break;
  }

  return [convertLocalTime(start).getTime(), convertLocalTime(end).getTime()];
};
export const widgetFilterToSLFilter = (
  filters: (WidgetFilter | FilterDefinition)[] = []
): MeetingInstanceFilters => {
  let mapped: MeetingInstanceFilters = {};

  const mapValue = (
    key: string,
    filterValue: string | number | boolean | string[] | undefined
  ) => {
    let value: typeof filterValue;
    switch (typeof filterValue) {
      case "boolean":
        value = filterValue;
        break;
      case "string":
      case "number":
        value = Boolean(filterValue) ? filterValue : undefined;
        break;
      case "object":
        Array.isArray(filterValue) && filterValue.every((v) => Boolean(v))
          ? (value = filterValue)
          : (value = undefined);
      default:
        value = filterValue;
        break;
    }

    return getMappedValue(
      mapped,
      key as keyof MeetingInstanceFilters,
      value
    ) as MeetingInstanceFilters;
  };

  filters.forEach((f) => {
    switch (f.filterType as FilterTypes & AdditionalFilterTypes) {
      case "status":
        let tmpStatus: string[] = [];
        const statusValue = f.payload
          ? Array.isArray(f.payload)
            ? f.payload
            : typeof f.payload === "object" &&
              Object.keys(f.payload).length === 0
            ? undefined
            : [f.payload]
          : [f.filterValue];

        if (statusValue?.includes("InProgress")) {
          statusValue
            ?.filter((s) => s !== "InProgress")
            .map((f) => tmpStatus.push(f as string));
          tmpStatus.push(...inProgressFilter);
        } else {
          statusValue?.map((f) => tmpStatus.push(f as string));
        }
        mapped = mapValue("status", tmpStatus);
        break;
      case "assignee":
        mapped = mapValue("producerSearchType", f.filterValue);
        break;
      case "purpose":
        let tmpPurpose: string[] = [];

        const purposeArray = f.payload
          ? Array.isArray(f.payload)
            ? f.payload
            : typeof f.payload === "object" &&
              Object.keys(f.payload).length === 0
            ? undefined
            : [f.payload]
          : [f.filterValue];
        const certification = purposeArray?.find((p) => p === "Certification");
        if (certification) {
          purposeArray
            ?.filter((s) => s !== "Certification")
            .map((f) => tmpPurpose.push(f as string));
          tmpPurpose.push(...certificationFilter);
        } else {
          purposeArray?.map((f) => tmpPurpose.push(f as string));
        }
        mapped = mapValue("purpose", tmpPurpose);
        break;
      case "type":
        const typeValue = Array.isArray(f.payload)
          ? f.payload
          : Boolean(f.filterValue)
          ? [f.filterValue]
          : undefined;
        mapped = mapValue("type", typeValue as string[]);
        break;
      case "watchList":
        mapped = mapValue("watchList", Boolean(f.filterValue));
        break;
      case TIME_FILTER_SEARCH_KEY:
        const [start, end] = getTimesForMeetingsFilter(
          f.filterValue as MeetingsTimeFilter,
          f.payload as DateInterval
        );
        mapped = mapValue("start.after", start);
        mapped = mapValue("end.before", end);
        break;
      case "other":
        let otherFilterValue = f.filterValue as MeetingsOtherFilter;
        if (otherFilterValue === "VIP") mapped = mapValue("vip", true);
        else
          mapped = mapValue("scheduledMeeting.producerService", [
            otherFilterValue as ScheduledMeetingProducerService,
          ]);
        break;
      case "accountId":
        mapped = mapValue(
          f.filterType as keyof MeetingInstanceFilters,
          Boolean(f.filterValue) ? [f.filterValue] : undefined
        );
        break;
      case "producerIds":
        mapped = mapValue(
          f.filterType as keyof MeetingInstanceFilters,
          f.payload as string[]
        );
        break;
      case "scheduledMeeting.meetingSource":
        mapped = mapValue(
          "scheduledMeeting.meetingSource",
          Array.isArray(f.payload) && Boolean(f.payload) ? f.payload : undefined
        );
        break;
      default:
        mapped = mapValue(
          f.filterType as keyof MeetingInstanceFilters,
          Boolean(f.filterValue) ? f.filterValue : undefined
        );
        break;
    }
  });

  return mapped;
};

export const widgetFilterToSearchParams = (
  filters?: (WidgetFilter | FilterDefinition)[]
) => {
  const asSL: any = widgetFilterToSLFilter(filters);
  const timeFilter = filters?.find(
    ({ filterType }) => (filterType as FilterTypes) === TIME_FILTER_SEARCH_KEY
  );
  if (timeFilter !== undefined) {
    asSL[TIME_FILTER_SEARCH_KEY] = encodeURI(timeFilter.filterValue);
  }
  if (decodeURI(asSL[TIME_FILTER_SEARCH_KEY]) != "Custom time period") {
    delete asSL["start.after"];
    delete asSL["end.before"];
  }
  return objectToSearchParams(asSL);
};

const SLFilterToWidgetFilter = (
  slFilters: MeetingInstanceFilters
): FilterDefinition[] => {
  const filters: FilterDefinition[] = [];
  if (slFilters.status !== undefined) {
    let tmp: string[] = [];
    if (
      slFilters.status.includes("InProgress") &&
      slFilters.status.includes("InProgressPartial")
    )
      tmp.push("InProgress");
    slFilters.status
      .filter((v) => v !== "InProgress" && v !== "InProgressPartial")
      .map((v) => tmp.push(v));
    filters.push({
      filterType: "status",
      filterValue: "",
      payload: tmp,
    });
  }
  if (slFilters.producerSearchType !== undefined)
    filters.push({
      filterType: "assignee",
      filterValue: slFilters.producerSearchType,
    });
  if (
    slFilters.producerIds !== undefined &&
    slFilters.producerSearchType === "ASSIGNED_TO"
  )
    filters.push({
      filterType: "producerIds",
      filterValue: "",
      payload: slFilters.producerIds,
    });
  if (slFilters.purpose !== undefined) {
    filters.push({
      filterType: "purpose",
      filterValue: "",
      payload: slFilters.purpose,
    });
  }
  if (slFilters.type !== undefined)
    filters.push({
      filterType: "type",
      filterValue: "",
      payload: slFilters.type,
    });
  if (slFilters["vip"] !== undefined) {
    filters.push({
      filterType: "vip",
      filterValue: slFilters["vip"] ? "true" : "false",
    });
  }
  if (slFilters["scheduledMeeting.producerService"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.producerService",
      filterValue: slFilters["scheduledMeeting.producerService"],
    });
  }
  if (slFilters["start.after"] && slFilters["end.before"]) {
    let customValue: MeetingsTimeFilter = "Custom time period";
    filters.push({
      filterType: TIME_FILTER_SEARCH_KEY,
      filterValue: customValue,
      payload: {
        startDate: slFilters["start.after"],
        endDate: slFilters["end.before"],
      },
    });
  }
  if (slFilters["accountId"] !== undefined) {
    filters.push({
      filterType: "accountId",
      filterValue: Array.isArray(slFilters["accountId"])
        ? slFilters["accountId"][0]
        : slFilters["accountId"],
    });
  }
  if (slFilters["participants.room.location.name"] !== undefined) {
    filters.push({
      filterType: "participants.room.location.name",
      filterValue: slFilters["participants.room.location.name"],
    });
  }
  if (slFilters["participants.room.name"] !== undefined) {
    filters.push({
      filterType: "participants.room.name",
      filterValue: slFilters["participants.room.name"],
    });
  }
  if (slFilters["includeAllWithOpenTickets"] !== undefined) {
    filters.push({
      filterType: "includeAllWithOpenTickets",
      filterValue: slFilters["includeAllWithOpenTickets"] ? "true" : "false",
    });
  }
  if (slFilters["scheduledMeeting.meetingSource"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.meetingSource",
      filterValue: "",
      payload: slFilters["scheduledMeeting.meetingSource"],
    });
  }
  if (slFilters["meetingId"] !== undefined) {
    filters.push({
      filterType: "meetingId",
      filterValue: slFilters["meetingId"][0],
    });
  }
  if (slFilters["scheduledMeeting.evcsMeetingId"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.evcsMeetingId",
      filterValue: slFilters["scheduledMeeting.evcsMeetingId"],
    });
  }
  if (slFilters["scheduledMeeting.emailParticipants.email"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.emailParticipants.email",
      filterValue: slFilters["scheduledMeeting.emailParticipants.email"],
    });
  }
  if (slFilters["scheduledMeeting.requestorEmail"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.requestorEmail",
      filterValue: slFilters["scheduledMeeting.requestorEmail"],
    });
  }
  if (slFilters["scheduledMeeting.ownerEmail"] !== undefined) {
    filters.push({
      filterType: "scheduledMeeting.ownerEmail",
      filterValue: slFilters["scheduledMeeting.ownerEmail"],
    });
  }
  if (slFilters["wrappedUp"] !== undefined) {
    filters.push({
      filterType: "wrappedUp",
      filterValue: slFilters["wrappedUp"] ? "true" : "false",
    });
  }
  if (slFilters["title"] !== undefined) {
    filters.push({
      filterType: "title",
      filterValue: slFilters["title"],
    });
  }

  return filters;
};

const searchToSLFilter = (params: URLSearchParams): MeetingInstanceFilters => {
  const filters: MeetingInstanceFilters = {};
  const temp = {
    vip: params.get("vip"),
    "scheduledMeeting.producerService": params.get(
      "scheduledMeeting.producerService"
    ),
    "participants.room.location.name": params.get(
      "participants.room.location.name"
    ),
    "participants.room.name": params.get("participants.room.name"),
    accountId: params.getAll("accountId"),
    "scheduledMeeting.meetingSource": params.getAll(
      "scheduledMeeting.meetingSource"
    ),
  };

  asString(filters, params, [
    "title",
    "producerSearchType",
    "scheduledMeeting.producerService",
    "scheduledMeeting.extendedProperties.EMSID",
    "scheduledMeeting.evcsMeetingId",
    "scheduledMeeting.emailParticipants.email",
    "scheduledMeeting.requestorEmail",
    "scheduledMeeting.ownerEmail",
    // "participants.room.name",
    // "participants.room.location.name",
  ]);
  asNumber(filters, params, ["start.after", "end.before"]);
  asBoolean(filters, params, ["vip", "includeAllWithOpenTickets", "wrappedUp"]);
  asNonEmptyArray(filters, params, [
    "meetingId",
    "status",
    "producerIds",
    "type",
    "purpose",
    "scheduledMeeting.meetingSource",
  ]);
  if (temp["participants.room.name"] !== null) {
    filters["participants.room.name"] = temp["participants.room.name"];
  }
  if (temp["participants.room.location.name"] !== null) {
    filters["participants.room.location.name"] =
      temp["participants.room.location.name"];
  }
  if (temp["scheduledMeeting.meetingSource"].length > 0)
    filters["scheduledMeeting.meetingSource"] =
      temp["scheduledMeeting.meetingSource"];
  if (temp.accountId.length > 0) filters.accountId = temp.accountId;
  return filters;
};

const searchToWidgetFilter = (params: URLSearchParams): FilterDefinition[] => {
  let definition = SLFilterToWidgetFilter(searchToSLFilter(params));
  let timeFilterType = decodeURI(
    params.get(TIME_FILTER_SEARCH_KEY) ?? "For the next 12 hours"
  );
  let timeFilter = definition.find(
    (f) => f.filterType === TIME_FILTER_SEARCH_KEY
  );
  if (
    !definition.find((f) => {
      return f.filterType === TIME_FILTER_SEARCH_KEY;
    }) &&
    timeFilterType !== "Custom time period"
  ) {
    definition.push({
      filterType: TIME_FILTER_SEARCH_KEY,
      filterValue: timeFilterType,
    });
  } else {
    definition = definition.filter((f) => {
      return f.filterType !== TIME_FILTER_SEARCH_KEY;
    });

    definition.push({
      filterType: TIME_FILTER_SEARCH_KEY,
      filterValue: timeFilterType,
      payload:
        timeFilter && timeFilterType === "Custom time period"
          ? timeFilter.payload
          : undefined,
    });
  }
  return definition;
};

export default {
  fromDefinition: {
    toSL: widgetFilterToSLFilter,
    toSearchParams: widgetFilterToSearchParams,
  },
  fromSL: {
    toDefinition: SLFilterToWidgetFilter,
    toSearchParams: (m: MeetingInstanceFilters) => SLFilterToSearch(m),
  },
  fromSearchParams: {
    toSL: searchToSLFilter,
    toDefinition: searchToWidgetFilter,
  },
};
